/**
 * Trovva — Date Formatting Utility
 *
 * Single source of truth for date display across the app.
 * All dates displayed to users should use these helpers.
 * Format: MM/DD/YYYY (US standard per business requirement).
 */

/**
 * Format a date as MM/DD/YYYY
 * @param {string|Date} dateInput - ISO string, Date object, or date string
 * @returns {string} Formatted date or '—' if invalid
 */
export function formatDate(dateInput) {
  if (!dateInput) return '—';
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Format a date as MM/DD/YYYY h:mm AM/PM
 * @param {string|Date} dateInput
 * @returns {string}
 */
export function formatDateTime(dateInput) {
  if (!dateInput) return '—';
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return '—';
  const date = formatDate(d);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} ${time}`;
}

/**
 * Format a date as short: Apr 23 or Apr 23, 2026
 * @param {string|Date} dateInput
 * @param {boolean} includeYear
 * @returns {string}
 */
export function formatDateShort(dateInput, includeYear = false) {
  if (!dateInput) return '—';
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return '—';
  const opts = includeYear
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' };
  return d.toLocaleDateString('en-US', opts);
}

/**
 * Format a date relative to today: "Today", "Tomorrow", "Yesterday", "3 days ago", or MM/DD/YYYY
 * @param {string} dateStr - ISO date string (YYYY-MM-DD)
 * @returns {string}
 */
export function formatDateRelative(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff < -1 && diff > -14) return `${Math.abs(diff)} days ago`;
  if (diff > 1 && diff < 14) return `in ${diff} days`;
  return formatDate(d);
}
